import { useContext, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery, useApolloClient } from '@apollo/client';
import {
  Table,
  Loader,
  Button,
  IColumn,
  SvgIcon,
  IWebinar,
  Checkbox,
  Typography,
  getDuration,
  ICheckedData,
  CheckBoxGroup,
  LabelWithIcon,
  ILearningTableData,
  IDashboardGroupDetailsCount,
} from '@ascd/witsby-components';
import RunningWithErrorsIcon from '@mui/icons-material/RunningWithErrors';
import { Box, Divider, Grid, useTheme, Tooltip, IconButton } from '@mui/material';
import * as FileSaver from 'file-saver';
import { debounce, get, isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import { LearnosityReportTable } from '@components/LearnosityReportTable';
import WhatHasUserMissed from '@components/WhatHasUserMissed';
import { ASCD_ID } from '@constants';
import { AppContext } from '@contexts/appContext';
import { DashboardContext, IDetailsPage, eDashboardActionType } from '@contexts/dashboardContext';
import DASHBOARD_USER_DETAILS from '@graphql/schema/dashboardUserDetails.graphql';
import GET_EBOOK_URL from '@graphql/schema/getEbookUrl.graphql';
import LOG_EVENTS from '@graphql/schema/mutations/logEvents.graphql';
import {
  ICourse,
  IEBook,
  IElMagazine,
  IFilterCourseList,
  ILearningTable,
  IReportData,
  ISelectedReport,
  ITableFilter,
} from '@types';
import {
  showToast,
  dateFormat,
  handleGraphqlError,
  getDashboardProgress,
  DASHBOARD_COUNTS_TYPES,
  DASHBOARD_FILTER_TYPES,
  progressStatusColorCodes,
  getDashboardProgressStatus,
  DASHBOARD_FILTER_DEFAULT_CHECKED_DATA,
  pdfGenerator,
} from '@utils';

const AdminDashboardLearningsDetails = () => {
  const apolloClient = useApolloClient();
  const Theme = useTheme();

  const {
    state: { currentInstitution },
  } = useContext(AppContext);

  const {
    dispatch,
    state: { detailsPage, sideBarApiFilter },
  } = useContext(DashboardContext);

  const {
    assignedBy,
    internalId1,
    selectedInstitution,
    createdByOrganizationId,
    dates: { startDate, endDate },
  } = sideBarApiFilter;

  const { detailsPageData } = detailsPage as IDetailsPage;
  const { learningsData } = detailsPageData;
  const [openFilter, setOpenFilter] = useState(false);
  const [checkedList, setCheckedList] = useState<ICheckedData[]>(
    detailsPageData.status || DASHBOARD_FILTER_DEFAULT_CHECKED_DATA,
  );
  const [confirmCheckedList, setConfirmCheckedList] = useState<ICheckedData[]>(
    detailsPageData.status || DASHBOARD_FILTER_DEFAULT_CHECKED_DATA,
  );
  const [checkedTypeList, setCheckedTypeList] = useState<ICheckedData[]>(
    detailsPageData.type || DASHBOARD_FILTER_TYPES,
  );
  const [confirmCheckedTypeList, setConfirmCheckedTypeList] = useState<ICheckedData[]>(
    detailsPageData.type || DASHBOARD_FILTER_TYPES,
  );
  const [whatHasUserMissedData, setWhatHasUserMissedData] = useState<{
    userId: string;
    collectionId: string;
  } | null>(null);
  const [selectedReportData, setSelectedReportData] = useState<ISelectedReport | null>(null);
  const [isAssessmentReportOpen, setAssessmentReportOpen] = useState(false);
  const [filter, setFilter] = useState<ITableFilter>({
    page: 0,
    limit: 20,
    searchText: '',
    sortDirection: 'DESC',
    sortField: 'createdAt',
    searchField: [
      'data.learningItemCollection.title',
      'data.learningItem.title',
      'data.learningItemCollection.title',
      'data.title',
      'data.userName',
    ],
  });

  // Handle institution selection filter for ASCD admins
  const currentSelectedInstitution = useMemo(() => {
    if (!isEmpty(selectedInstitution)) return selectedInstitution;

    return {
      value: currentInstitution._id,
      label: currentInstitution.name,
    };
  }, [currentInstitution._id, currentInstitution.name, selectedInstitution]);

  const variables = {
    filterWithSortArray: {
      limit: filter.limit,
      page: filter.page,
      searchField: [
        'data.learningItemCollection.title',
        'data.learningItem.title',
        'data.learningItemCollection.title',
        'data.title',
      ],
      searchText: filter.searchText,
      sortDirections: [filter.sortDirection, 'ASC'],
      sortFields: [filter.sortField, 'userName'],
    },
    isPortFolio: false,
    endDate,
    startDate,
    assignedBy,
    titles: null,
    managerIds: null,
    organizationId:
      currentSelectedInstitution?.label === 'ALL' ? '' : currentSelectedInstitution.value,
    createdByOrganizationId: isEmpty(createdByOrganizationId)
      ? [currentSelectedInstitution.value, ASCD_ID]
      : createdByOrganizationId,
    schoolIds:
      currentSelectedInstitution?.label === 'ALL' || currentSelectedInstitution?.label === 'ASCD'
        ? null
        : internalId1.map((internalId) => internalId?.internalId),
    userIds: learningsData?.map((item: IDashboardGroupDetailsCount) => item.userId),
    completionStatus: confirmCheckedList.map((item) => item._id),
    learningType: confirmCheckedTypeList.map((item) => item._id),
  };

  const { loading, data: dashBoardUserDetailsData } = useQuery(DASHBOARD_USER_DETAILS, {
    variables,
    fetchPolicy: 'network-only',
  });

  const dashboardUserDetailsList = get(dashBoardUserDetailsData, 'dashBoardUserDetails', {
    totalCount: 0,
    data: [],
    totalAllocatedTime: 0,
  });

  const [logEvents] = useMutation(LOG_EVENTS);

  // when click on ebooks it's fetch url and open pdf
  const onOpenEBook = async (item?: IEBook | ICourse | IFilterCourseList) => {
    if (!Object.prototype.hasOwnProperty.call(item, 'LockLizardBookId')) return;
    if ('LockLizardBookId' in (item as ICourse | IEBook | IFilterCourseList)) {
      const response = await apolloClient.query({
        query: GET_EBOOK_URL,
        variables: { docId: (item as IEBook)?.LockLizardBookId },
      });
      if (response?.data?.getEbookUrl) {
        logEvents({
          variables: {
            data: item,
            logType: 'EBOOK',
            actionType: 'VIEWED',
          },
        });
        window.open(response.data.getEbookUrl, '_blank');
      }
    }
  };

  const filterView = () => (
    <Box sx={{ width: 300, borderRadius: '0.625rem' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Box
          sx={{
            color: (theme) => theme.palette.grey[100],
            fontSize: (theme) => theme.font.size.REGULAR,
          }}>
          Filter
        </Box>
        <Box
          sx={(theme) => ({
            fontSize: theme.font.size.X_SMALL,
            color: theme.palette.primary.main,
            cursor: 'pointer',
          })}
          onClick={() => {
            setCheckedList([]);
            setCheckedTypeList([]);
          }}>
          Clear all
        </Box>
      </Box>
      <Divider sx={{ my: '0.625rem' }} />
      <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box
          sx={{
            fontSize: (theme) => theme.font.size.SMALL,
          }}>
          Status
        </Box>

        <Checkbox
          label="Select all"
          checked={checkedList.length === DASHBOARD_FILTER_DEFAULT_CHECKED_DATA.length}
          onHandleChange={(e) => {
            if (e.target.checked) {
              setCheckedList(DASHBOARD_FILTER_DEFAULT_CHECKED_DATA);
            } else {
              setCheckedList([]);
            }
          }}
        />
      </Box>
      {/* Error Message  */}
      {checkedList.length === 0 && (
        <Box
          sx={{
            px: 2,
            color: (theme) => theme.palette.error.light,
            fontSize: (theme) => theme.font.size.XX_SMALL,
          }}>
          Please select one or more status and click Apply.
        </Box>
      )}
      <Box sx={{ px: 2 }}>
        <CheckBoxGroup
          checkedList={checkedList}
          boxSx={{ border: 'none', padding: '0px' }}
          checkedData={DASHBOARD_FILTER_DEFAULT_CHECKED_DATA}
          handleCheck={(id, name) => {
            if (checkedList?.some((item) => item._id === id)) {
              setCheckedList((checkedList || [])?.filter((item) => item._id !== id));
            } else {
              setCheckedList([...(checkedList || []), { _id: id, name }]);
            }
          }}
        />
      </Box>
      <Divider sx={{ my: '0.625rem' }} />

      {/* Learning Type  */}
      <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ fontSize: (theme) => theme.font.size.SMALL }}>Type</Box>
        <Checkbox
          label="Select all"
          checked={checkedTypeList.length === DASHBOARD_FILTER_TYPES.length}
          onHandleChange={(e) => {
            if (e.target.checked) {
              setCheckedTypeList(DASHBOARD_FILTER_TYPES);
            } else {
              setCheckedTypeList([]);
            }
          }}
        />
      </Box>
      {/* Error Message  */}
      {checkedTypeList.length === 0 && (
        <Box
          sx={{
            px: 2,
            color: (theme) => theme.palette.error.light,
            fontSize: (theme) => theme.font.size.XX_SMALL,
          }}>
          Please select one or more content type and click Apply.
        </Box>
      )}
      <Box sx={{ px: 2 }}>
        <CheckBoxGroup
          checkedData={DASHBOARD_FILTER_TYPES}
          checkedList={checkedTypeList}
          boxSx={{ border: 'none', padding: '0px' }}
          handleCheck={(id, name) => {
            if (checkedTypeList?.some((item) => item._id === id)) {
              setCheckedTypeList((checkedTypeList || [])?.filter((item) => item._id !== id));
            } else {
              setCheckedTypeList([...(checkedTypeList || []), { _id: id, name }]);
            }
          }}
        />
      </Box>
      <Divider sx={{ my: '0.625rem' }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, gap: '0.5rem' }}>
        <Button
          size="small"
          onClick={() => {
            setOpenFilter(false);
            setCheckedList(confirmCheckedList);
            setCheckedTypeList(confirmCheckedTypeList);
          }}
          sx={{
            borderRadius: '0.5rem',
            px: '0.625rem',
            '&': {
              color: (theme) => theme.palette.grey[300],
              borderColor: (theme) => theme.palette.grey[300],
            },
            '&:hover': {
              color: (theme) => theme.palette.white[100],
              borderColor: (theme) => theme.palette.grey[300],
              backgroundColor: (theme) => theme.palette.grey[300],
            },
          }}>
          Cancel
        </Button>
        <Button
          disabled={checkedList.length === 0 || checkedTypeList.length === 0}
          size="small"
          sx={{
            borderRadius: '0.5rem',
            px: '0.625rem',
            '&, &.Mui-disabled': {
              color: (theme) => theme.palette.primary.light,
            },
          }}
          onClick={() => {
            setOpenFilter(false);
            setFilter({ ...filter, page: 0 });
            setConfirmCheckedList(checkedList);
            setConfirmCheckedTypeList(checkedTypeList);
          }}>
          Apply
        </Button>
      </Box>
    </Box>
  );

  const todayDate = new Date().getTime();

  const learningColumn: IColumn[] = [
    {
      key: 'userName',
      name: 'Learner',
    },
    {
      key: 'title',
      name: 'Title',
      width: '35%',
      render: (_, value) => {
        const { data, type, courseStatus } = value as ILearningTable;
        return (
          <Box>
            {(type === 'COURSE' || type === 'STRATEGY') && (
              <Box>{(data as ILearningTableData)?.learningItemCollection?.title}</Box>
            )}
            {type === 'ITEM' && (
              <Box>{(data as ILearningTableData)?.learningItem?.title || 'Untitled'}</Box>
            )}
            {type === 'LIST' && (
              <Box>{(data as ILearningTableData)?.learningItemCollection?.title}</Box>
            )}
            {type === 'WEBINAR' && (
              <Box
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  '&:hover,&:focus': {
                    textDecoration: 'underline',
                    outline: 'none',
                    color: (theme) => theme.palette.primary.light,
                  },
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    if ((data as IWebinar)?.slug) {
                      window.open(
                        `https://www.ascd.org/el/${(data as IWebinar)?.slug}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }
                  }
                }}
                onClick={() => {
                  if ((data as IWebinar)?.slug) {
                    window.open(
                      `https://www.ascd.org/el/${(data as IWebinar)?.slug}`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                }}>
                {(data as IWebinar)?.title}
              </Box>
            )}
            {type === 'EBOOK' && (
              <Box
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  '&:hover,&:focus': {
                    textDecoration: 'underline',
                    outline: 'none',
                    color: (theme) => theme.palette.primary.light,
                  },
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    onOpenEBook(data as IEBook);
                  }
                }}
                onClick={() => onOpenEBook(data as IEBook)}>
                {(data as IEBook)?.title}
              </Box>
            )}
            {type === 'EL_MAGAZINE' && (
              <Box
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  '&:hover,&:focus': {
                    textDecoration: 'underline',
                    outline: 'none',
                    color: (theme) => theme.palette.primary.light,
                  },
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    window.open(
                      `https://www.ascd.org/el/${(data as IElMagazine)?.slug}`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                }}
                onClick={() => {
                  if ((data as IElMagazine)?.slug) {
                    window.open(
                      `https://www.ascd.org/el/${(data as IElMagazine)?.slug}`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                }}>
                {(data as IElMagazine)?.title}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      key: 'progress',
      name: 'Progress (in hours)',
      render: (_, value) => {
        const { data, type } = value as ILearningTable;
        return (
          <Box>
            <Box
              sx={{
                fontFamily: (theme) => [theme.font.family.PRIMARY].join(','),
                fontSize: (theme) => theme.font.size.X_SMALL,
                fontWeight: (theme) => theme.font.weight.BOLD,
              }}>
              {(type === 'COURSE' || type === 'STRATEGY') && (
                <Box
                  sx={{
                    background: (theme) => theme.palette.grey[500],
                    width: '8.75rem',
                    height: '1.438rem',
                  }}>
                  {((data as ILearningTableData)?.userLearningItemCollection
                    ?.userCollectionStatus === 'ADD' ||
                    (data as ILearningTableData)?.userLearningItemCollection
                      ?.userCollectionStatus === 'ENROLL') && (
                    <Box
                      sx={{
                        width: '100%',
                        color: '#FFFFFF',
                        height: '1.438rem',
                        background: '#e2252b',
                      }}>
                      <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                        0m/
                        {getDuration(
                          (data as ILearningTableData)?.userLearningItemCollection
                            ?.totalAllocatedTime || 0,
                          false,
                        )}
                      </Box>
                    </Box>
                  )}
                  {((data as ILearningTableData)?.userLearningItemCollection
                    ?.userCollectionStatus === 'RESUME' ||
                    (data as ILearningTableData)?.userLearningItemCollection
                      ?.userCollectionStatus === 'START') && (
                    <Box
                      sx={{
                        background: 'yellow',
                        width: `${Math.round(
                          ((((data as ILearningTableData)?.userLearningItemCollection
                            ?.totalAllocatedTime || 0) -
                            ((data as ILearningTableData)?.userLearningItemCollection
                              ?.totalEarnedTime || 0)) /
                            ((data as ILearningTableData)?.userLearningItemCollection
                              ?.totalAllocatedTime || 1)) *
                            100,
                        )}%`,
                        height: '1.438rem',
                        color: '#000000',
                      }}>
                      <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                        {getDuration(
                          ((data as ILearningTableData)?.userLearningItemCollection
                            ?.totalAllocatedTime || 0) -
                            ((data as ILearningTableData)?.userLearningItemCollection
                              ?.totalEarnedTime || 0),
                          false,
                        )}
                        /
                        {getDuration(
                          (data as ILearningTableData)?.userLearningItemCollection
                            ?.totalAllocatedTime || 0,
                          false,
                        )}
                      </Box>
                    </Box>
                  )}
                  {(data as ILearningTableData)?.userLearningItemCollection
                    ?.userCollectionStatus === 'COMPLETED' && (
                    <Box
                      sx={{
                        background: '#03c04a',
                        width: '100%',
                        height: '1.438rem',
                        color: '#FFFFFF',
                      }}>
                      <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                        {getDuration(
                          ((data as ILearningTableData)?.userLearningItemCollection
                            ?.totalAllocatedTime || 0) -
                            ((data as ILearningTableData)?.userLearningItemCollection
                              ?.totalEarnedTime || 0),
                          false,
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {type === 'ITEM' && (
                <Box>
                  <Box
                    sx={{
                      background: '#E1E1E1',
                      width: '8.75rem',
                      height: '1.438rem',
                    }}>
                    {(data as ILearningTableData)?.trackingLearningItem?.action === 'STARTED' && (
                      <Box
                        sx={{
                          background: 'yellow',
                          width: `${Math.round(
                            (((data as ILearningTableData)?.trackingLearningItem
                              ?.totalConsumedTime || 0) /
                              ((data as ILearningTableData)?.trackingLearningItem?.earnedTime ||
                                0)) *
                              100,
                          )}%`,
                          height: '1.438rem',
                          color: '#000000',
                        }}>
                        <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                          {getDuration(
                            (data as ILearningTableData)?.trackingLearningItem?.totalConsumedTime ||
                              0,
                            false,
                          )}
                          /
                          {getDuration(
                            (data as ILearningTableData)?.trackingLearningItem?.earnedTime || 0,
                            false,
                          )}
                        </Box>
                      </Box>
                    )}
                    {(data as ILearningTableData)?.trackingLearningItem?.action === 'COMPLETED' && (
                      <Box
                        sx={{
                          background: '#03c04a',
                          width: '100%',
                          height: '1.438rem',
                          color: '#FFFFFF',
                        }}>
                        <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                          {getDuration(
                            (data as ILearningTableData)?.trackingLearningItem?.earnedTime || 0,
                            false,
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {type === 'LIST' && (
                <Box>
                  <Box
                    sx={{
                      background: '#E1E1E1',
                      width: '8.75rem',
                      height: '1.438rem',
                    }}>
                    {((data as ILearningTableData)?.userLearningItemCollection
                      ?.userCollectionStatus === 'ADD' ||
                      (data as ILearningTableData)?.userLearningItemCollection
                        ?.userCollectionStatus === 'ENROLL') && (
                      <Box
                        sx={{
                          background: '#e2252b',
                          width: '100%',
                          height: '1.438rem',
                          color: '#FFFFFF',
                        }}>
                        <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                          0m/
                          {getDuration(
                            (data as ILearningTableData)?.userLearningItemCollection
                              ?.totalAllocatedTime || 0,
                            false,
                          )}
                        </Box>
                      </Box>
                    )}
                    {((data as ILearningTableData)?.userLearningItemCollection
                      ?.userCollectionStatus === 'RESUME' ||
                      (data as ILearningTableData)?.userLearningItemCollection
                        ?.userCollectionStatus === 'START') && (
                      <Box
                        sx={{
                          background: 'yellow',
                          width: `${Math.round(
                            ((((data as ILearningTableData)?.userLearningItemCollection
                              ?.totalAllocatedTime || 0) -
                              ((data as ILearningTableData)?.userLearningItemCollection
                                ?.totalEarnedTime || 0)) /
                              ((data as ILearningTableData)?.userLearningItemCollection
                                ?.totalAllocatedTime || 1)) *
                              100,
                          )}%`,
                          height: '1.438rem',
                          color: '#000000',
                        }}>
                        <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                          {getDuration(
                            ((data as ILearningTableData)?.userLearningItemCollection
                              ?.totalAllocatedTime || 0) -
                              ((data as ILearningTableData)?.userLearningItemCollection
                                ?.totalEarnedTime || 0),
                            false,
                          )}
                          /
                          {getDuration(
                            (data as ILearningTableData)?.userLearningItemCollection
                              ?.totalAllocatedTime || 0,
                            false,
                          )}
                        </Box>
                      </Box>
                    )}
                    {(data as ILearningTableData)?.userLearningItemCollection
                      ?.userCollectionStatus === 'COMPLETED' && (
                      <Box
                        sx={{
                          background: '#03c04a',
                          width: '100%',
                          height: '1.438rem',
                          color: '#FFFFFF',
                        }}>
                        <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                          {getDuration(
                            (data as ILearningTableData)?.userLearningItemCollection
                              ?.totalAllocatedTime || 0,
                            false,
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            {(type === 'WEBINAR' || type === 'EBOOK' || type === 'EL_MAGAZINE') && <Box>NA</Box>}
          </Box>
        );
      },
    },
    {
      key: 'status',
      name: 'Status',
      render: (_, value) => {
        const { data, type } = value as ILearningTable;
        return (
          <Box>
            {(type === 'COURSE' || type === 'STRATEGY') && (
              <Box>
                {((data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                  'ADD' ||
                  (data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                    'ENROLL') && <Box>Zero Progress</Box>}
                {((data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                  'RESUME' ||
                  (data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                    'START') && <Box>In Progress</Box>}
                {(data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                  'COMPLETED' && <Box>Completed</Box>}
              </Box>
            )}
            {type === 'ITEM' && (
              <Box>
                {(data as ILearningTableData)?.trackingLearningItem?.action === 'STARTED' && (
                  <Box>In Progress</Box>
                )}
                {(data as ILearningTableData)?.trackingLearningItem?.action === 'COMPLETED' && (
                  <Box>Completed</Box>
                )}
              </Box>
            )}
            {type === 'LIST' && (
              <Box>
                {((data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                  'ADD' ||
                  (data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                    'ENROLL') && <Box>Zero Progress</Box>}
                {((data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                  'RESUME' ||
                  (data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                    'START') && <Box>In Progress</Box>}
                {(data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
                  'COMPLETED' && <Box>Completed</Box>}
              </Box>
            )}
            {(type === 'WEBINAR' || type === 'EBOOK' || type === 'EL_MAGAZINE') && (
              <Box>Viewed</Box>
            )}
          </Box>
        );
      },
    },
    {
      key: 'type',
      name: 'Type',
      render: (_, value) => (
        <Box>
          {(value as ILearningTable)?.type === 'COURSE' && <Box>Course</Box>}
          {(value as ILearningTable)?.type === 'ITEM' && <Box>Learning Item</Box>}
          {(value as ILearningTable)?.type === 'LIST' && <Box>Learning List</Box>}
          {(value as ILearningTable)?.type === 'STRATEGY' && <Box>Strategy</Box>}
          {(value as ILearningTable)?.type === 'WEBINAR' && <Box>Webinar</Box>}
          {(value as ILearningTable)?.type === 'EBOOK' && <Box>EBook</Box>}
          {(value as ILearningTable)?.type === 'EL_MAGAZINE' && <Box>El Magazine</Box>}
        </Box>
      ),
    },
    {
      key: 'createdAt',
      name: 'Last Access',
      render: (_, value) => {
        const { type, data, createdAt } = value as ILearningTable;
        if (
          (type === 'COURSE' || type === 'LIST' || type === 'STRATEGY') &&
          ((data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
            'ADD' ||
            (data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ===
              'ENROLL')
        ) {
          return <Box>-</Box>;
        }
        return (
          <Box>
            <Box>
              {DateTime.fromJSDate(new Date(createdAt as string)).toFormat(`''LL'/'dd'/'yyyy'`)}
            </Box>
          </Box>
        );
      },
      isSorting: true,
      ...(filter.sortField === 'createdAt' && {
        sortType: filter.sortDirection,
      }),
    },
    {
      key: 'assessment-certificate',
      name: '',
      align: 'center',
      headerAlign: 'center',
      render: (_, value) => {
        const { data, type, userName } = value as ILearningTable;
        const userLearningItemCollection = (data as ILearningTableData)?.userLearningItemCollection;

        const courseName =
          (data as ILearningTableData)?.learningItemCollection?.title || 'Untitled';
        const issuedOn = (data as ILearningTableData)?.userLearningItemCollection?.certificate
          ?.issued_on;
        const updatedAt = (data as ILearningTableData)?.userLearningItemCollection?.updatedAt;
        const date = DateTime.fromJSDate(new Date((issuedOn || updatedAt) as string))
          .setZone('utc')
          .toFormat('MM-dd-yyyy');
        const hours = getDuration(
          ((data as ILearningTableData)?.userLearningItemCollection?.totalAllocatedTime || 0) -
            ((data as ILearningTableData)?.userLearningItemCollection?.totalEarnedTime || 0),
          false,
        );

        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {userLearningItemCollection &&
              userLearningItemCollection.userCollectionStatus !== 'COMPLETED' &&
              (type === 'COURSE' || type === 'LIST' || type === 'STRATEGY') && (
                <Tooltip arrow title="What has the user missed?">
                  <IconButton
                    onClick={() => {
                      setWhatHasUserMissedData({
                        userId: userLearningItemCollection.userId,
                        collectionId: userLearningItemCollection.learningItemCollectionId,
                      });
                    }}>
                    <RunningWithErrorsIcon sx={(theme) => ({ fill: theme.palette.error.light })} />
                  </IconButton>
                </Tooltip>
              )}
            {userLearningItemCollection && type === 'COURSE' && (
              <>
                {userLearningItemCollection.certificate?.url &&
                  userName &&
                  courseName &&
                  hours &&
                  date && (
                    <Tooltip describeChild title="Certificate" arrow>
                      <IconButton>
                        <SvgIcon
                          icon="BADGE"
                          sx={{
                            fill:
                              !(data as ILearningTableData)?.userLearningItemCollection?.certificate
                                ?.expired_on ||
                              todayDate <=
                                new Date(
                                  (data as ILearningTableData)?.userLearningItemCollection
                                    ?.certificate?.expired_on as string,
                                ).getTime()
                                ? '#00A77E'
                                : '#EF4060',
                          }}
                          onClick={() => {
                            const certificateUrl = userLearningItemCollection.certificate?.url;
                            if (certificateUrl) {
                              pdfGenerator(userName, courseName, `${hours}`, date, certificateUrl);
                            }
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                {get(
                  value as ILearningTable,
                  'data.learningItemCollection.settings.completionFactor.assessments',
                  [],
                ).length > 0 && (
                  <Tooltip describeChild title="Assessment" arrow>
                    <IconButton
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: (theme) => theme.font.size.SMALL,
                      }}>
                      <SvgIcon
                        icon="ASSESSMENT"
                        sx={{ fill: 'none' }}
                        onClick={() => {
                          setSelectedReportData({
                            searchData: (value as ILearningTable)?.userName,
                            reportData: {
                              type: (value as ILearningTable)?.type,
                              id: ((value as ILearningTable)?.data as ILearningTableData)
                                ?.learningItemCollection?._id,
                              title: ((value as ILearningTable)?.data as ILearningTableData)
                                ?.learningItemCollection?.title,
                            },
                          });
                          setAssessmentReportOpen(true);
                        }}
                      />
                      {get(
                        value as ILearningTable,
                        'data.userLearningItemCollection.assessments.length',
                        0,
                      )
                        ? `(${get(
                            value as ILearningTable,
                            'data.userLearningItemCollection.completedAssessments.length',
                            0,
                          )}/${userLearningItemCollection.assessments?.length})`
                        : ''}
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        );
      },
    },
  ];

  const [dashboardUserDetailsCSV, { loading: isCSVLoading }] = useLazyQuery(
    DASHBOARD_USER_DETAILS,
    { fetchPolicy: 'network-only' },
  );

  const exportToCSV = async () => {
    if (!isCSVLoading) {
      dashboardUserDetailsCSV({
        variables: {
          ...variables,
          filterWithSortArray: {
            searchField: [
              'data.learningItemCollection.title',
              'data.learningItem.title',
              'data.learningItemCollection.title',
              'data.title',
              'data.userName',
            ],
            searchText: filter.searchText,
            sortDirections: [filter.sortDirection, 'ASC'],
            sortFields: [filter.sortField, 'userName'],
            page: 0,
            limit: dashboardUserDetailsList.totalCount,
          },
        },
      })
        .then((res) => {
          const datalist = res.data.dashBoardUserDetails.data.map((item: ILearningTable) => ({
            Learner: item?.userName,
            Title:
              (item?.data as ILearningTableData)?.learningItemCollection?.title ||
              (item?.data as ILearningTableData)?.learningItem?.title ||
              (item?.data as ILearningTableData)?.learningItemCollection?.title ||
              (item?.data as IEBook)?.title ||
              (item?.data as IWebinar)?.title ||
              (item?.data as IElMagazine)?.title ||
              'Untitled',
            'Progress (in hours)': getDashboardProgress(
              item.type,
              (item.data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus ||
                (item.data as ILearningTableData)?.trackingLearningItem?.action,
              item.type === 'ITEM'
                ? getDuration(
                    (item.data as ILearningTableData)?.trackingLearningItem?.totalConsumedTime || 0,
                    false,
                  )
                : getDuration(
                    ((item.data as ILearningTableData)?.userLearningItemCollection
                      ?.totalAllocatedTime || 0) -
                      ((item.data as ILearningTableData)?.userLearningItemCollection
                        ?.totalEarnedTime || 0),
                    false,
                  ),
              item.type === 'ITEM'
                ? getDuration(
                    (item.data as ILearningTableData)?.trackingLearningItem?.earnedTime || 0,
                    false,
                  )
                : getDuration(
                    (item.data as ILearningTableData)?.userLearningItemCollection
                      ?.totalAllocatedTime || 0,
                    false,
                  ),
            ),
            Status: getDashboardProgressStatus(
              item.type,
              (item.data as ILearningTableData)?.trackingLearningItem?.action ||
                (item.data as ILearningTableData)?.userLearningItemCollection?.userCollectionStatus,
            ),
            Type: DASHBOARD_COUNTS_TYPES[item.type],
            'Last Access':
              (item.type === 'COURSE' || item.type === 'LIST' || item.type === 'STRATEGY') &&
              ((item.data as ILearningTableData)?.userLearningItemCollection
                ?.userCollectionStatus === 'ADD' ||
                (item.data as ILearningTableData)?.userLearningItemCollection
                  ?.userCollectionStatus === 'ENROLL')
                ? '-'
                : dateFormat({ date: item?.createdAt, format: 'dd-MMM-yyyy' }),
          }));
          const fileType =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          const fileExtension = '.xlsx';
          const ws = XLSX.utils.json_to_sheet(datalist);
          const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const data = new Blob([excelBuffer], { type: fileType });
          FileSaver.saveAs(
            data,
            `${
              learningsData?.length > 1
                ? `Learnings Details (${dateFormat({ date: startDate, format: 'dd-MMM-yyyy' })} - ${dateFormat(
                    { date: endDate, format: 'dd-MMM-yyyy' },
                  )})`
                : `${learningsData[0]?.name} (${dateFormat({
                    date: startDate,
                    format: 'dd-MMM-yyyy',
                  })} - ${dateFormat({ date: endDate, format: 'dd-MMM-yyyy' })})`
            } ${fileExtension}`,
          );
          showToast('Downloaded Successfully');
        })
        .catch(handleGraphqlError);
    }
  };

  if (isAssessmentReportOpen) {
    return (
      <LearnosityReportTable
        reportData={selectedReportData?.reportData as IReportData}
        setAssessmentReportOpen={setAssessmentReportOpen}
        searchData={`${selectedReportData?.searchData || 'Learning'}` as string}
      />
    );
  }

  return (
    <Box sx={{ mt: 1, mb: 5 }}>
      {whatHasUserMissedData && (
        <WhatHasUserMissed
          {...whatHasUserMissedData}
          handleClose={() => setWhatHasUserMissedData(null)}
        />
      )}
      <Box>
        <Grid container spacing={3}>
          <Grid item xl={12} sm={12} xs={12}>
            <Table
              isSearchEnable
              isFilterEnable
              loading={loading}
              page={filter.page}
              apiPaginationEnable
              openFilter={openFilter}
              pageSize={filter.limit}
              columns={learningColumn}
              searchValue={filter.searchText}
              searchPlaceHolder="Search Title"
              activeSortField={filter.sortField}
              filterPopoverChildren={filterView()}
              data={dashboardUserDetailsList.data}
              onOpenFilter={() => setOpenFilter(true)}
              count={dashboardUserDetailsList.totalCount}
              getRowSx={(row) => {
                if (row?.courseStatus === 'UNPUBLISHED' || row?.courseStatus === 'ARCHIVED') {
                  return {
                    background: (theme) => theme.palette.grey.A100,
                  };
                }
                return undefined;
              }}
              dataEmptyChildren={<Box>No learnings found</Box>}
              onCloseFilter={() => {
                setOpenFilter(false);
                setCheckedList(confirmCheckedList);
                setCheckedTypeList(confirmCheckedTypeList);
              }}
              handlePageChange={(_, newPage) => {
                setFilter({ ...filter, page: newPage });
              }}
              onHandleSearch={debounce((e) => {
                setFilter({ ...filter, page: 0, searchText: e.target.value });
              }, 500)}
              handleRowsPerPageChange={(e) => {
                setFilter({ ...filter, page: 0, limit: parseInt(e.target.value, 10) });
              }}
              onSortClick={(key, sortType) => {
                setFilter({
                  ...filter,
                  page: 0,
                  sortField: key as string,
                  sortDirection: sortType,
                });
              }}
              headerChildren={
                <Box>
                  <LabelWithIcon
                    title="Learnings Details"
                    iconName="MOVE_UP"
                    iconDirection="left"
                    iconColorHex="#FFFFFF"
                    sx={{ color: '#FFFFFF' }}
                    onClick={() => {
                      dispatch({
                        type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
                        data: null,
                      });
                    }}
                    IconTitleDistance={1}
                    iconSx={{ transform: 'rotate(270deg)' }}
                  />
                </Box>
              }
              headerExtra={
                dashboardUserDetailsList.totalCount > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    {isCSVLoading ? (
                      <Loader
                        sx={{ color: '#FFFFFF', ml: (theme) => theme.spaces.LARGE }}
                        size={20}
                      />
                    ) : (
                      <Tooltip describeChild title="Download the report below" arrow>
                        <IconButton
                          sx={{
                            ml: (theme) => theme.spaces.REGULAR,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onClick={() => exportToCSV()}
                          aria-label="collection type">
                          <SvgIcon
                            icon="FILE_DOWNLOAD"
                            viewBox="0 0 22 29"
                            sx={{ fill: '#FFFF' }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )
              }
              aboveTableHeaderChildren={
                <Box
                  sx={{
                    p: 1.2,
                    pt: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                  <Box
                    sx={{
                      background: '#e7f9f4',
                      borderRadius: '50px',
                      fontSize: '11px',
                      display: 'flex',
                      gap: '30px',
                      pl: '5px',
                      height: '31px',
                      width: '340px',
                      justifyContent: 'center',
                    }}>
                    {progressStatusColorCodes.map((progressStatusColorCode) => (
                      <Box
                        key={progressStatusColorCode.colorCode}
                        sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Box
                          sx={{
                            width: '16px',
                            height: '15px',
                            background: progressStatusColorCode.colorCode,
                            border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                          }}
                        />
                        <Typography sx={{ fontSize: '11px' }} text={progressStatusColorCode.text} />
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '1.563rem' }}>
                    <LabelWithIcon
                      title="Certificate"
                      iconName="BADGE"
                      iconDirection="left"
                      iconColorHex={Theme.palette.primary.dark}
                      isClickable={false}
                      sx={{
                        fontWeight: (theme) => theme.font.weight.LIGHT,
                      }}
                    />
                    <LabelWithIcon
                      title="Assessment"
                      iconName="ASSESSMENT"
                      iconColorHex="none"
                      iconDirection="left"
                      isClickable={false}
                      sx={{
                        fontWeight: (theme) => theme.font.weight.LIGHT,
                      }}
                    />
                  </Box>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AdminDashboardLearningsDetails;
